import { useState } from "react";
import { toast } from "sonner";
import { X, Send, Loader2, FileText, ExternalLink, AlertTriangle } from "lucide-react";
import { sendQuoteEmail, previewPdfUrl } from "@/lib/api";

export default function SendEmailDialog({ quote, emailStatus, onClose, onSent }) {
  const isFR = quote.language === "fr";
  const subjectPrefix = isFR ? "Votre devis de livre" : "Your book quote";
  const defaultMessage = isFR
    ? `Bonjour ${quote.client_name || ""},\n\nVeuillez trouver ci-joint votre devis ${quote.quote_number}${
        quote.project_title ? ` pour ${quote.project_title}` : ""
      }. Vous pouvez aussi le consulter et l'accepter en ligne via le lien dans cet e-mail.\n\nN'hésitez pas si vous avez des questions.\n\nCordialement`
    : `Hi ${quote.client_name || "there"},\n\nPlease find attached your quote ${quote.quote_number}${
        quote.project_title ? ` for ${quote.project_title}` : ""
      }. You can also view and accept it online using the link in this email.\n\nLet me know if you have any questions.\n\nThanks!`;
  const [to, setTo] = useState(quote.client_email || "");
  const [subject, setSubject] = useState(
    `${subjectPrefix} ${quote.quote_number}` +
      (quote.project_title ? ` - ${quote.project_title}` : "")
  );
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  const configured = emailStatus?.configured;

  const handleSend = async () => {
    if (!to) {
      toast.error("Please enter a recipient email");
      return;
    }
    setSending(true);
    try {
      const res = await sendQuoteEmail(quote.id, { to, subject, message });
      toast.success(`Quote emailed to ${res.to}`);
      onSent?.(res);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Send failed";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="send-email-dialog">
      <div className="bg-white border border-[#050505] w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="border-b border-[#050505] px-6 py-5 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-label mb-1">Send Quote</div>
            <h2 className="font-display font-bold text-2xl tracking-tight uppercase">
              Email to client
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#f5f5f5]" data-testid="close-send-dialog" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!configured && (
            <div className="border border-[#FF3333] bg-[#FFF5F5] p-4 text-sm" data-testid="email-not-configured">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-[#FF3333] mt-0.5 shrink-0" strokeWidth={1.75} />
                <div>
                  <div className="font-bold text-[#FF3333] mb-1">Email is not configured.</div>
                  <div className="text-[#525252] text-xs leading-relaxed">
                    Add your <code className="font-mono">RESEND_API_KEY</code> to{" "}
                    <code className="font-mono">backend/.env</code> and restart the backend.
                    Free key at <a className="ghost-link" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a>.
                  </div>
                </div>
              </div>
            </div>
          )}

          <label className="block">
            <span className="swiss-label">To (recipient email)</span>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com"
              className="swiss-input"
              data-testid="send-to-input"
            />
          </label>

          <label className="block">
            <span className="swiss-label">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="swiss-input"
              data-testid="send-subject-input"
            />
          </label>

          <label className="block">
            <span className="swiss-label">Message</span>
            <textarea
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="swiss-input resize-y"
              data-testid="send-message-input"
            />
          </label>

          <div className="border border-[#050505] bg-[#f5f5f5] p-4 flex items-start gap-3">
            <FileText className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-1">
                Attachment: Quote-{quote.quote_number}.pdf
              </div>
              <div className="text-xs text-[#525252] leading-relaxed">
                Client-view PDF (no printer cost, no markup). The email also includes a button linking to the public quote page where they can Accept or Decline.
              </div>
            </div>
            <a
              href={previewPdfUrl(quote.id)}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.18em] font-bold ghost-link inline-flex items-center gap-1 shrink-0"
              data-testid="preview-pdf-link"
            >
              Preview <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
            </a>
          </div>

          {emailStatus?.sender && (
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#525252]">
              Sending as: <span className="font-mono normal-case tracking-normal">{emailStatus.sender}</span>
            </div>
          )}
        </div>

        <div className="border-t border-[#050505] px-6 py-4 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-secondary" data-testid="cancel-send-btn">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !configured || !to}
            className="btn-primary"
            data-testid="confirm-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
            {sending ? "Sending…" : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}
